import Parse from "parse";

import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../header/GameContext";
import { CHAT_DATA } from "./chatData";

import "./chat.css";

const ChatBoxType2 = forwardRef(function ChatBoxType2(
  {
    dataWord = "",
    DateVal,
    titImgClass = "",
    chatClass = "chat_box4",
    onFinish,
  },
  ref,
) {
  const { DB_TABLE, playerName, interpolate } = useGame();
  const navigate = useNavigate();

  const [currentKey, setCurrentKey] = useState(DateVal);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleContents, setVisibleContents] = useState([]);
  const [activeButtons, setActiveButtons] = useState([]);

  const chatBoxRef = useRef(null);
  const timeoutRef = useRef(null);
  const pendingUserMsgRef = useRef(null);
  const currentKeyRef = useRef(DateVal);

  /* =========================
     외부 resume
  ========================= */
  useImperativeHandle(ref, () => ({
    resume() {
      setCurrentIndex((prev) => prev + 1);
    },
  }));

  /* =========================
     currentKey 변경
  ========================= */
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    currentKeyRef.current = currentKey;

    if (pendingUserMsgRef.current) {
      const msg = pendingUserMsgRef.current;
      pendingUserMsgRef.current = null;
      if (msg && msg.type) {
        setVisibleContents((prev) => [...prev, msg]);
      }
    }

    setCurrentIndex(0);
    setActiveButtons([]);
  }, [currentKey]);

  /* =========================
     채팅 진행
  ========================= */
  useEffect(() => {
    const rawContentsNow = Array.isArray(currentKeyRef.current)
      ? currentKeyRef.current
      : CHAT_DATA[currentKeyRef.current] || [];

    const contentsNow = rawContentsNow.map((item) => ({
      ...item,
      text: interpolate(item.text, { playerName }),
      chat_tit: item.chat_tit
        ? interpolate(item.chat_tit, { playerName })
        : item.chat_tit,
    }));

    if (!contentsNow || currentIndex >= contentsNow.length) return;

    const item = contentsNow[currentIndex];
    if (!item || !item.type) return;

    /* ✅ choice로 넘어온 nextData의 첫 rightCharacter 스킵 (pendingUserMsg로 이미 출력) */
    if (
      item.type === "rightCharacter" &&
      currentIndex === 0 &&
      pendingUserMsgRef._wasPending
    ) {
      pendingUserMsgRef._wasPending = false;
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    /* 🔥 버튼 분리 */
    if (item.type === "button") {
      const btnGroup = [];
      let i = currentIndex;

      while (contentsNow[i] && contentsNow[i].type === "button") {
        btnGroup.push(contentsNow[i]);
        i++;
      }

      setActiveButtons(btnGroup);
      setCurrentIndex(i);
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (item && item.type) {
        setVisibleContents((prev) => [...prev, item]);
      }
      setCurrentIndex((prev) => prev + 1);
    }, 1000);

    return () => clearTimeout(timeoutRef.current);
  }, [currentIndex, currentKey]);

  /* =========================
     스크롤
  ========================= */
  useEffect(() => {
    if (!chatBoxRef.current) return;

    chatBoxRef.current.scrollTo({
      top: chatBoxRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [visibleContents, activeButtons]);

  /* =========================
     버튼 클릭
  ========================= */
  const onBtnClick = async (btn) => {
    if (!btn) return;

    const { action, nextData, label, route, state } = btn;

    setActiveButtons([]);

    if (nextData) {
      if (nextData === "tutorialExp_yes") {
        try {
          const GameUser = Parse.Object.extend(DB_TABLE);
          const query = new Parse.Query(GameUser);
          const objectId = localStorage.getItem("gameUserId");
          const user = await query.get(objectId);

          user.set("isTutorialExp", Boolean(true));
          await user.save();

          console.log("저장 성공:", user);
        } catch (error) {
          console.error("에러 코드:", error.code);
          console.error("에러 메시지:", error.message);
        }
      } else if (nextData === "tutorialExp_no") {
        try {
          const GameUser = Parse.Object.extend(DB_TABLE);
          const query = new Parse.Query(GameUser);
          const objectId = localStorage.getItem("gameUserId");
          const user = await query.get(objectId);

          user.set("isTutorialExp", Boolean(false));
          await user.save();

          console.log("저장 성공:", user);
        } catch (error) {
          console.error("에러 코드:", error.code);
          console.error("에러 메시지:", error.message);
        }
      }
    }

    /* 👉 choice: 유저 메시지 출력 */
    if (action === "choice") {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      pendingUserMsgRef.current = {
        type: "rightCharacter",
        chat_icon: "Me",
        chat_tit: playerName,
        talkType: "chat_b_right_Talk2",
        text: label,
        marType: "mb40",
      };
      pendingUserMsgRef._wasPending = true;

      setCurrentKey(nextData);

      return;
    }

    /* 👉 route */
    if (action === "route") {
      navigate(route, {
        state: {
          DateVal: route.replace("/", ""),
          ...state,
        },
      });
      return;
    }

    /* 👉 finish */
    if (action === "finish") {
      onFinish?.({ route, state });
    }
  };

  return (
    <div className={`${chatClass} mt24`} id="chat_box" ref={chatBoxRef}>
      {visibleContents.filter(Boolean).map((c, i) => {
        if (!c || !c.type) return null;

        if (c.type === "chatInfo_Talk1") {
          return (
            <div
              key={i}
              className={`chatInfo_Talk ${c.marType || ""}`}
              dangerouslySetInnerHTML={{ __html: c.text }}
            />
          );
        }

        if (c.type === "leftCharacter") {
          return (
            <div key={i} className={`chat_b_left ${c.marType || ""}`}>
              <div className="chat_b_l_left mr8">
                <span className={c.chat_icon}></span>
              </div>
              <div className="chat_b_l_right">
                <p className="chat_tit mb4">{c.chat_tit}</p>
                <div
                  className={c.talkType}
                  dangerouslySetInnerHTML={{ __html: c.text }}
                />
              </div>
            </div>
          );
        }

        if (c.type === "left_txt") {
          return (
            <div key={i} className={`chat_b_left ${c.marType || ""}`}>
              <div className="chat_b_l_left mr8" />
              <div className="chat_b_l_right">
                <div
                  className={c.talkType}
                  dangerouslySetInnerHTML={{ __html: c.text }}
                />
              </div>
            </div>
          );
        }

        if (c.type === "ImgOnly") {
          return (
            <div key={i} className={c.marType || ""}>
              <img src={c.src} className={c.className} alt="" />
            </div>
          );
        }

        /* ✅ 올바른 구조로 수정 */
        if (c.type === "rightCharacter") {
          return (
            <div key={i} className={`chat_b_right ${c.marType || ""}`}>
              <div className="chat_b_r_left mr8">
                <p className="chat_tit mb4">{c.chat_tit}</p>
                <div
                  className={c.talkType}
                  dangerouslySetInnerHTML={{ __html: c.text }}
                />
              </div>
              <div className="chat_b_r_right">
                <span className={c.chat_icon}></span>
              </div>
            </div>
          );
        }

        return null;
      })}

      {activeButtons.length > 0 && (
        <div id="box_ft">
          <div className="chat_btn_area">
            {activeButtons.map((btn, i) => (
              <button
                key={i}
                className={`inputBtn02 ${
                  Array.isArray(btn.classes)
                    ? btn.classes.join(" ")
                    : btn.classes || ""
                }`}
                onClick={() => onBtnClick(btn)}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default ChatBoxType2;
